import React from "react";
import { useId, useMemo } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Label, Separator } from ".";

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        "gap-4 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3 flex flex-col",
        className
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn("mb-1.5 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base", className)}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        "gap-5 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4 group/field-group @container/field-group flex w-full flex-col",
        className
      )}
      {...props}
    />
  );
}

const fieldVariants = cva("data-[invalid=true]:text-destructive gap-2 group/field flex w-full", {
  variants: {
    orientation: {
      vertical: "flex-col *:w-full [&>.sr-only]:w-auto",
      horizontal:
        "flex-row items-center *:data-[slot=field-label]:flex-auto has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
      responsive:
        "flex-col *:w-full [&>.sr-only]:w-auto @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:*:data-[slot=field-label]:flex-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
    },
  },
  defaultVariants: {
    orientation: "vertical",
  },
});

interface FieldState {
  invalid: boolean;
  orientation: "vertical" | "horizontal" | "responsive";
}

interface FieldControlProps {
  id: string;
  name?: string;
  "aria-invalid"?: true;
  "aria-describedby"?: string;
}

interface FieldProps
  extends Omit<React.ComponentProps<"div">, "placeholder" | "children">,
    VariantProps<typeof fieldVariants> {
  children?: React.ReactNode | ((controlProps: FieldControlProps, state: FieldState) => React.ReactNode);
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: string;
  name?: string;
}

function Field({
  className,
  orientation = "vertical",
  label,
  description,
  error,
  children,
  name,
  ...props
}: FieldProps) {
  const reactId = useId();
  const id = props.id ?? reactId;
  const showDescription = !error && description;
  const descriptionId = showDescription ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const controlProps = useMemo<FieldControlProps>(
    () => ({
      id,
      name,
      "aria-invalid": error ? true : undefined,
      "aria-describedby": [descriptionId, errorId].filter(Boolean).join(" ") || undefined,
    }),
    [id, descriptionId, errorId, error]
  );

  const state = useMemo(
    () => ({
      invalid: !!error,
      orientation: orientation!,
    }),
    [error, orientation]
  );

  const resolvedChildren = typeof children === "function" ? children(controlProps, state) : children;

  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      data-invalid={error ? true : undefined}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    >
      {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}
      {resolvedChildren}
      {showDescription && <FieldDescription id={descriptionId}>{description}</FieldDescription>}
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </div>
  );
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn("gap-0.5 group/field-content flex flex-1 flex-col leading-snug", className)}
      {...props}
    />
  );
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn(
        "has-data-checked:bg-primary/5 has-data-checked:border-primary/30 dark:has-data-checked:border-primary/20 dark:has-data-checked:bg-primary/10 gap-2 group-data-[disabled=true]/field:opacity-50 has-[>[data-slot=field]]:rounded-lg has-[>[data-slot=field]]:border *:data-[slot=field]:p-2.5 group/field-label peer/field-label flex w-fit leading-snug font-semibold",
        "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col",
        "text-foreground",
        className
      )}
      {...props}
    />
  );
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        "gap-2 text-sm font-medium group-data-[disabled=true]/field:opacity-50 flex w-fit items-center leading-snug",
        className
      )}
      {...props}
    />
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        "text-muted-foreground text-left text-sm [[data-variant=legend]+&]:-mt-1.5 leading-normal font-normal group-has-data-horizontal/field:text-balance",
        "last:mt-0 nth-last-2:-mt-1",
        "[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4",
        className
      )}
      {...props}
    />
  );
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn("-my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2 relative", className)}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="text-muted-foreground px-2 bg-background relative mx-auto block w-fit ignore-base-styling"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  );
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<"div"> & {
  errors?: Array<{ message?: string } | undefined>;
}) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [...new Map(errors.map(error => [error?.message, error])).values()];

    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn("text-destructive text-sm font-normal", className)}
      {...props}
    >
      {content}
    </div>
  );
}

Field.displayName = "Field";
FieldLabel.displayName = "FieldLabel";
FieldDescription.displayName = "FieldDescription";
FieldError.displayName = "FieldError";
FieldGroup.displayName = "FieldGroup";
FieldLegend.displayName = "FieldLegend";
FieldSeparator.displayName = "FieldSeparator";
FieldSet.displayName = "FieldSet";
FieldContent.displayName = "FieldContent";
FieldTitle.displayName = "FieldTitle";

const FieldNamespace = Object.assign(Field, {
  Label: FieldLabel,
  Description: FieldDescription,
  Error: FieldError,
  Group: FieldGroup,
  Legend: FieldLegend,
  Separator: FieldSeparator,
  Set: FieldSet,
  Content: FieldContent,
  Title: FieldTitle,
});

export { FieldNamespace as Field };
